import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
  createUDPUiTypeMapper,
} from '@wix/editor-elements-integrations';
import * as vectorImage from '@wix/thunderbolt-commons/dist/vectorImage';
import {
  castToInheritIfNeeded,
  withFallbackValues,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';

import type {
  BreadcrumbsDefinition,
  BreadcrumbsMapperProps,
  BreadcrumbsCSSVars,
  BreadcrumbsPropsCarmiData,
  IBreadcrumbsStateValues,
  SDKDataType,
} from '../Breadcrumbs.types';
import { flexToLogicalAlignment } from '../editor/panels/layout/flexToLogicalAlignmentMap';
import {
  ARIA_LABEL_NAMESPACE,
  ARIA_LABEL_KEY,
  ARIA_LABEL_DEFAULT,
} from '../constants';

export const props = withCompInfo<
  BreadcrumbsMapperProps,
  BreadcrumbsDefinition,
  BreadcrumbsPropsCarmiData
>()(
  [
    'compData',
    'compProps',
    'styleProperties',
    'compStylableClass',
    'isMobileView',
    'externalBaseUrl',
    'pagesMap',
    'hierarchyPagesMap',
    'mainPageId',
    'editorName',
    'experiments',
    'isDescendantOfBlocksWidget',
    'translate',
  ],
  (
    {
      compData,
      compProps,
      compStylableClass,
      isMobileView,
      externalBaseUrl,
      pagesMap,
      hierarchyPagesMap,
      mainPageId,
      editorName,
      isDescendantOfBlocksWidget,
      translate,
    },
    carmiData,
  ) => {
    const {
      separator,
      shouldWrap,
      showHomePage,
      showCurrentPage,
      showHomePageAsIcon,
      showOnlyPreviousPageOnMobile,
      itemsBeforeCollapse,
      itemsAfterCollapse,
    } = compProps;

    return {
      a11y: compData.a11y,
      hasPageHierarchyBreadcrumbs:
        !isDescendantOfBlocksWidget && editorName === 'Studio',
      svgString: carmiData.svgString,
      pagesMenuItems: carmiData.pagesMenuItems,
      pagesMap,
      hierarchyPagesMap,
      mainPageId,
      separator,
      shouldWrap,
      showHomePage,
      showCurrentPage,
      showHomePageAsIcon,
      itemsBeforeCollapse,
      itemsAfterCollapse,
      showOnlyPreviousPageOnMobile:
        isMobileView && showOnlyPreviousPageOnMobile,
      isMobile: isMobileView,
      stylableClassName: compStylableClass,
      externalBaseUrl,
      translations: {
        defaultAriaLabel:
          translate(ARIA_LABEL_NAMESPACE, ARIA_LABEL_KEY) || ARIA_LABEL_DEFAULT,
      },
    } satisfies BreadcrumbsMapperProps;
  },
);

const css = withCompInfo<BreadcrumbsCSSVars, BreadcrumbsDefinition>()(
  ['compData', 'compProps', 'styleProperties'],
  ({ compData, styleProperties }) => {
    const { align, horizontalMargin, verticalMargin } = styleProperties;
    const { direction = 'inherit' } = compData;

    return {
      '--horizontalMargin': `${horizontalMargin}px`,
      '--verticalMargin': `${verticalMargin}px`,
      '--alignment': align,
      '--direction': withSiteDirection(direction),
      height: 'auto', // component is not resizable vertically and should have content height
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [
          'direction',
          'alignment',
          'horizontalMargin',
          'verticalMargin',
          'align',
        ],
      },
      {
        // moving align from legacy value to new one
        sourceNamespace: 'styleProperties',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'alignment', target: 'align' }],
        enhancer: flexToLogicalAlignment,
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'compData',
        fields: ['direction'],
        enhancer: direction => castToInheritIfNeeded(direction, 'ltr'),
      },
    ]),
  ],
);

const sdkData = withCompInfo<SDKDataType, BreadcrumbsDefinition>()(
  ['topology'],
  ({ topology }) => {
    const { mediaRootUrl } = topology;
    const { getSvgBaseUrl } = vectorImage.buildSvgUrlFactory();

    return {
      mediaSvgUrl: getSvgBaseUrl(mediaRootUrl),
    } satisfies SDKDataType;
  },
);

const stateRefs = withStateRefsValues<keyof IBreadcrumbsStateValues>([
  'currentUrl',
  'pageId',
  'setVeloSeoTags',
  'previewMode',
]);

export const uiType = createUDPUiTypeMapper(
  'StylableComponent',
  'UDPComponent',
);

export default createComponentMapperModel({
  css,
  props,
  sdkData,
  stateRefs,
  uiType,
});
